// Shared boot data — accounts, projects, current user — fetched once for the
// whole app instead of by every page. Pages call useAccounts() / useProjects()
// instead of social.listAccounts() / auth.me() on mount, which removes the
// duplicate fetches that were causing flashing between routes.

import { createContext, useContext, useEffect, useRef, useState, type ReactNode } from "react";
import { social, type SocialAccount } from "./social";
import { auth } from "./apteva";
import { useStaticData } from "./static-data";
import { setProjectCookie, getCurrentProjectId } from "./project";

type Project = { id: string; name: string; slug: string; is_active: boolean; created_at: string; [key: string]: any };

type AppData = {
  user: any | null;
  projects: Project[];
  accounts: SocialAccount[];
  accountsLoading: boolean;
  authChecking: boolean;
  refreshAccounts: () => Promise<void>;
  refreshUser: () => Promise<void>;
  setUser: (u: any) => void;
};

const Ctx = createContext<AppData | null>(null);

export function AppDataProvider({ children }: { children: ReactNode }) {
  const ssrUser = useStaticData<any>("_user");
  const ssrAccounts = useStaticData<SocialAccount[]>("_accounts");

  const ssrRendered = typeof window === "undefined" || !!(window as any).__STATIC_DATA__;
  const [user, setUser] = useState<any>(ssrUser ?? null);
  const [projects, setProjects] = useState<Project[]>([]);
  const [accounts, setAccounts] = useState<SocialAccount[]>(ssrAccounts ?? []);
  // Treat "no SSR data + we have a user" as the only case that needs a load
  // spinner. Anonymous visitors on /login etc. shouldn't see loading state.
  const [accountsLoading, setAccountsLoading] = useState(!ssrAccounts && !!ssrUser);
  // Skip the auth-checking UI gate when SSR already gave us a user — avoids the blank-screen flash
  const [authChecking, setAuthChecking] = useState(ssrRendered ? false : !ssrUser);

  // Single-flight: don't refetch if a refresh is already in progress.
  const accountsInFlight = useRef<Promise<void> | null>(null);

  async function refreshAccounts() {
    if (accountsInFlight.current) return accountsInFlight.current;
    setAccountsLoading(true);
    accountsInFlight.current = (async () => {
      try {
        const res = await social.listAccounts({ limit: 100 });
        setAccounts(res.data || []);
      } catch {
        // Don't blow up on transient failures — pages can show their own empty/error UI
      } finally {
        setAccountsLoading(false);
        accountsInFlight.current = null;
      }
    })();
    return accountsInFlight.current;
  }

  async function refreshUser() {
    try {
      const res: any = await auth.me();
      setUser(res.user);
      const projs: Project[] = res.projects || [];
      setProjects(projs);
      if (projs.length > 0) {
        const cid = getCurrentProjectId();
        const valid = projs.some((p) => p.id === cid);
        if (!valid) setProjectCookie(projs[0].id);
      }
    } catch {
      setUser(null);
    } finally {
      setAuthChecking(false);
    }
  }

  // Boot: always verify user (even if SSR-rendered) so projects + cookie reflect server truth.
  useEffect(() => {
    refreshUser();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Boot accounts only after we know the user is authenticated, so anonymous
  // visitors on marketing / login pages don't hit the API and get a 401.
  useEffect(() => {
    if (user && !ssrAccounts && accounts.length === 0) {
      refreshAccounts();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user]);

  return (
    <Ctx.Provider
      value={{ user, projects, accounts, accountsLoading, authChecking, refreshAccounts, refreshUser, setUser }}
    >
      {children}
    </Ctx.Provider>
  );
}

export function useAppData(): AppData {
  const v = useContext(Ctx);
  if (!v) throw new Error("useAppData must be used within AppDataProvider");
  return v;
}

export function useAccounts() {
  const { accounts, accountsLoading, refreshAccounts } = useAppData();
  return { accounts, loading: accountsLoading, refresh: refreshAccounts };
}
